import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { ProgressSpinner } from "primereact/progressspinner";
import { useRef } from "react";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import { ByProductStockSummary, SubCategoryStockSummary } from "../../models/oilProduction-model";
import { productionStockExport } from "./productionstockExport";
import "./StockSummaryDashboard.scss";

const StockSummaryDashboard: React.FC = () => {
    const [byProductStocks, setByProductStocks] = useState<ByProductStockSummary[]>([]);
    const [subCategoryStocks, setSubCategoryStocks] = useState<SubCategoryStockSummary[]>([]);
    const [posOptions, setPosOptions] = useState<{ label: string; value: number }[]>([]);
    const [selectedPosIds, setSelectedPosIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const op = useRef<OverlayPanel>(null);
    const [selectedAllocation, setSelectedAllocation] = useState<ByProductStockSummary | null>(null);

    useEffect(() => {
        fetchMUPlants();
    }, []);

    useEffect(() => {
        if (selectedPosIds.length > 0) {
            fetchAllStocks();
        } else {
            setByProductStocks([]);
            setSubCategoryStocks([]);
        }
    }, [selectedPosIds]);

    const fetchMUPlants = async () => {
        try {
            const res = await oilProductionService.GetMUPlantName();
            if (res) {
                const options = res.map((mu: any) => ({
                    label: mu.address2 ?? mu.posName ?? `Unit-${mu.posID}`,
                    value: Number(mu.posID),
                }));
                setPosOptions(options);
                setSelectedPosIds(options.map((o: { value: number }) => o.value));
            }
        } finally {
            setInitialLoading(false);
        }
    };

    const fetchAllStocks = async () => {
        try {
            setLoading(true);
            const [productRes, subCatRes] = await Promise.all([
                oilProductionService.GetByProductStock(selectedPosIds),
                oilProductionService.GetSubCategoryStock(selectedPosIds)
            ]);
            setByProductStocks(productRes);
            setSubCategoryStocks(subCatRes);
        } catch (error) {
            console.error("Error fetching stocks:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportStockData = async () => {
        try {
            setExporting(true);
            await productionStockExport(byProductStocks, subCategoryStocks, selectedPosIds);
        } catch (error) {
            console.error("Error exporting stock data:", error);
        } finally {
            setExporting(false);
        }
    };

    const pendingStockTemplate = (rowData: ByProductStockSummary) => {
        const val = Math.max(0, rowData.pendingLitres);
        const severity = val > 20 ? "success" : val > 0 ? "warning" : "danger";
        const displayUnit = rowData.unit || 'L';
        return (
            <div className="flex justify-content-center align-items-center gap-2 w-full">
                <Tag value={`${val.toFixed(2)} ${displayUnit}`} severity={severity} className="px-3 py-2 border-round-xl shadow-1" />
                {val < 10 && val > 0 && <i className="pi pi-exclamation-triangle text-orange-500" title="Low Stock"></i>}
            </div>
        );
    };

    const totalStockTemplate = (rowData: ByProductStockSummary) => {
        const val = Math.max(0, rowData.totalAvailableStock);
        const severity = val > 20 ? "success" : val > 0 ? "warning" : "danger";
        const displayUnit = rowData.unit || 'L';
        return (
            <div className="flex justify-content-center align-items-center gap-2 w-full">
                <Tag value={`${val.toFixed(2)} ${displayUnit}`} severity={severity} className="px-3 py-2 border-round-xl shadow-1" />
            </div>
        );
    };

    const stockValueTemplate = (value: number, unit: string) => {
        const val = Math.max(0, value);
        return (
            <span className="font-semibold text-700">
                {val.toFixed(2)} <small className="text-500">{unit || 'L'}</small>
            </span>
        );
    };

    const allocatedStockTemplate = (rowData: ByProductStockSummary) => {
        const val = Math.max(0, rowData.allocatedStock);
        const hasBreakdown = rowData.allocatedProducts && rowData.allocatedProducts.length > 0;
        
        return (
            <div className="flex align-items-center gap-3 py-2">
                <Tag value={`${val.toFixed(2)} ${rowData.unit || 'L'}`} severity="info" className="px-3 border-round-xl shadow-1 font-bold" />
                {hasBreakdown && (
                    <Button 
                        icon="pi pi-info-circle" 
                        text 
                        rounded 
                        size="small" 
                        onClick={(e) => {
                            setSelectedAllocation(rowData);
                            op.current?.toggle(e);
                        }} 
                        className="p-button-secondary text-blue-500"
                        tooltip="View Allocation Breakdown"
                        tooltipOptions={{ position: 'top' }}
                    />
                )}
            </div>
        );
    };

    if (initialLoading) {
        return (
            <div className="flex justify-content-center align-items-center h-screen bg-white">
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" fill="var(--surface-ground)" animationDuration=".5s" />
            </div>
        );
    }

    return (
        <div className="stock-summary-dashboard">
            {/* Header Implementation matching Reward Awareness Dashboard */}
            <div className="dashboardHeaderWrapper">
                <div className="dashboardHeaderIcon">
                    <i className="pi pi-chart-bar"></i>
                </div>
                <div>
                    <h1 className="dashboardHeaderTitle">Production Stock Summary Dashboard</h1>
                    <p className="dashboardHeaderSubtitle">Monitor real-time oil production and inventory levels</p>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <FileActionButton action="export"
                        className="text-black border-round"
                        onClick={handleExportStockData}
                        icon="pi pi-file-excel"
                        text
                        raised
                        disabled={byProductStocks.length === 0 && subCategoryStocks.length === 0}
                    >
                        Export Production Stock
                    </FileActionButton>
                </div>
            </div>

            {/* Filter Section - Moved to Left */}
            <div className="grid">
                <div className="col-12 lg:col-6">
                    <div className="control-section mb-0">
                        <div className="pos-selector-wrapper shadow-1">
                            <label>Manufacturing Unit Selection</label>
                            <MultiSelect
                                value={selectedPosIds}
                                options={posOptions}
                                onChange={(e) => setSelectedPosIds(e.value)}
                                placeholder="Select Units"
                                maxSelectedLabels={3}
                                className="w-full border-none shadow-none"
                                display="chip"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid mt-4">
                {/* Product Wise Stock Summary */}
                <div className="col-12">
                    <div className="summary-card">
                        <div className="card-title-container">
                            <i className="pi pi-box text-blue-500"></i>
                            <h3>Oil Production & Batch Overview</h3>
                        </div>
                        <DataTable
                            value={byProductStocks}
                            loading={loading}
                            responsiveLayout="scroll"
                            className="p-datatable-sm mt-3"
                            stripedRows
                            emptyMessage="No product stock data found for selected units."
                            rowHover
                        >
                            <Column field="byProductName" header="By-product " sortable bodyClassName="font-bold text-900 cell-name" />
                            <Column field="totalProducedLitres" header="Total Produced" align="center" sortable headerStyle={{ minWidth: '160px' }} body={(r: ByProductStockSummary) => stockValueTemplate(r.totalProducedLitres, r.unit)} />
                            <Column field="pendingLitres" align="center" sortable headerStyle={{ minWidth: '160px' }}
                                header={
                                    <div className="flex flex-column align-items-center">
                                        <span> oil Production Available Stock</span>
                                        <small className="text-600 font-semibold" style={{ fontSize: '0.7rem', textTransform: 'none', marginTop: '2px' }}>(Yet to create batch)</small>
                                    </div>
                                }
                                body={pendingStockTemplate}
                            />
                            <Column field="usedInBatchLitres" header="Used in Batch" align="center" sortable headerStyle={{ minWidth: '160px' }} body={(r: ByProductStockSummary) => stockValueTemplate(r.usedInBatchLitres, r.unit)} />
                            <Column field="totalAvailableStock" align="center" sortable headerStyle={{ minWidth: '200px' }}
                                header={
                                    <div className="flex flex-column align-items-center">
                                        <span>Product Batch Available Stock</span>
                                        <small className="text-600 font-semibold" style={{ fontSize: '0.7rem', textTransform: 'none', marginTop: '2px' }}>(Yet to inward)</small>
                                    </div>
                                }
                                body={totalStockTemplate}
                            />
                            <Column field="allocatedStock" header="Allocated Details" sortable headerStyle={{ minWidth: '240px' }} body={allocatedStockTemplate} />
                        </DataTable>
                    </div>
                </div>
            </div>

            <OverlayPanel ref={op} dismissable style={{ width: '400px' }} className="shadow-4 border-round-xl">
                {selectedAllocation && (
                    <div className="p-3">
                        <div className="flex align-items-center gap-2 mb-3 border-bottom-1 surface-border pb-2">
                            <i className="pi pi-list text-blue-500 font-bold"></i>
                            <span className="font-bold text-900">Allocation Breakdown</span>
                        </div>
                        <div className="flex flex-column gap-2">
                            {selectedAllocation.allocatedProducts?.map(p => (
                                <div key={p.productId} className="allocated-product-pill w-full flex justify-content-between align-items-center shadow-none hover:bg-gray-100 p-2 border-round">
                                    <span className="p-name text-sm text-700">{p.productName}</span>
                                    <span className="p-vol font-bold text-green-600 border-none pl-3">{p.allocatedVolume} <small className="text-400">{selectedAllocation.unit || 'L'}</small></span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 pt-2 border-top-1 surface-border flex justify-content-between align-items-center">
                            <span className="text-600 text-xs font-semibold uppercase">Total Allocated</span>
                            <span className="font-bold text-blue-600">{selectedAllocation.allocatedStock.toFixed(2)} {selectedAllocation.unit || 'L'}</span>
                        </div>
                    </div>
                )}
            </OverlayPanel>
        </div>
    );
};

export default StockSummaryDashboard;